import { useState, useCallback, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom'
import Header from './components/Header'
import WelcomePage from './components/WelcomePage'
import Game from './components/Game'
import StatsModal from './components/StatsModal'
import SocialShare from './components/SocialShare'
import { getUserId } from './utils/analytics'
import { getCurrentGameNumber, parseDateFromStorage } from './utils/dateUtils'
import './App.css'

// Wrapper component to handle search params for the Game component
function GameWrapper({ onGameNumberChange, onGameStatusChange }: { 
  onGameNumberChange: (gameNumber: number) => void,
  onGameStatusChange?: (isCompleted: boolean, gameResult?: { isWin: boolean, playersRevealed: number, totalPlayers: number, theme: string, guessedCorrectly?: string, currentStreak: number }) => void
}) {
  const [searchParams] = useSearchParams()
  const dateParam = searchParams.get('date')
  const reviewParam = searchParams.get('review')
  
  const gameDate = dateParam ? parseDateFromStorage(dateParam) : undefined
  const isReview = reviewParam === 'true'
  
  return (
    <Game 
      onGameNumberChange={onGameNumberChange}
      onGameStatusChange={onGameStatusChange}
      gameDate={gameDate}
      isReview={isReview}
    />
  )
}

function App() {
  const [showStats, setShowStats] = useState<boolean>(false)
  const [showSocialShare, setShowSocialShare] = useState<boolean>(false)
  const [currentGameNumber, setCurrentGameNumber] = useState<number | null>(null)
  const [showShareButton, setShowShareButton] = useState<boolean>(false)
  const [gameResult, setGameResult] = useState<{ isWin: boolean, playersRevealed: number, totalPlayers: number, theme: string, guessedCorrectly?: string, currentStreak: number } | null>(null)
  
  const handleStatsClick = useCallback(() => {
    setShowStats(true)
  }, [])

  const handleStatsClose = useCallback(() => {
    setShowStats(false)
  }, [])
  
  const handleShareClick = useCallback(() => {
    setShowSocialShare(true)
  }, [])
  
  const handleShareClose = useCallback(() => {
    setShowSocialShare(false)
  }, [])
  
  const handleGameNumberChange = useCallback((gameNumber: number) => {
    setCurrentGameNumber(gameNumber)
  }, [])
  
  const handleGameStatusChange = useCallback((isCompleted: boolean, result?: { isWin: boolean, playersRevealed: number, totalPlayers: number, theme: string, guessedCorrectly?: string, currentStreak: number }) => {
    setShowShareButton(isCompleted)
    setGameResult(result || null)
  }, [])

  // Load current game number and monitor for date changes
  useEffect(() => {
    // Initialize user ID (creates GUID if doesn't exist)
    getUserId();
    
    // Set initial game number
    setCurrentGameNumber(getCurrentGameNumber())
    
    // Use a ref to track the current date across interval calls
    const currentDateRef = { current: new Date().toDateString() }
    
    // Monitor for date changes to update header game number
    const interval = setInterval(() => {
      const newDate = new Date().toDateString()
      if (newDate !== currentDateRef.current) {
        // Date has changed - update game number
        console.log('App: Date change detected:', currentDateRef.current, '->', newDate)
        currentDateRef.current = newDate
        
        const newGameNumber = getCurrentGameNumber()
        setCurrentGameNumber(newGameNumber)
      }
    }, 5000) // Check every 5 seconds for date changes

    return () => clearInterval(interval)
  }, [])
  return (
    <Router>
      <div className="App">
        <Header 
          onStatsClick={handleStatsClick} 
          onShareClick={handleShareClick}
          gameNumber={currentGameNumber}
          showShareButton={showShareButton}
        />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<WelcomePage />} />
            <Route path="/play" element={<GameWrapper onGameNumberChange={handleGameNumberChange} onGameStatusChange={handleGameStatusChange} />} />
          </Routes>
        </main>
        <StatsModal isOpen={showStats} onClose={handleStatsClose} />
        {gameResult && (
          <SocialShare
            isVisible={showSocialShare}
            onClose={handleShareClose}
            gameResult={gameResult}
          />
        )}
      </div>
    </Router>
  )
}

export default App
